import { addNotificationsComponent } from '../../initializers/notifications-initializer';
import { Injectable } from '@angular/core';
import {
  AuthService,
  LuigiCoreService,
  Theme,
  ThemingService,
  userSettingsLocalStorage,
} from '@openmfp/portal-ui-lib';
import { setTheme } from '@ui5/webcomponents-base/dist/config/Theme.js';

export const DEFAULT_THEME_ID = 'sap_horizon';

export const staticThemes = [
  {
    id: 'sap_horizon',
    name: 'Morning Horizon',
    description: 'Use in regular office environment',
  },
  {
    id: 'sap_horizon_dark',
    name: 'Evening Horizon',
    description: 'Use in dimmed environments',
  },
  {
    id: 'sap_horizon_hcw',
    name: 'High Contrast Morning Horizon',
    description:
      'Optimized contrast and accessibility for extremely dark environments',
  },
  {
    id: 'sap_horizon_hcb',
    name: 'High Contrast Evening Horizon',
    description:
      'Optimized contrast and accessibility for extremely bright environments',
  },
];

@Injectable({
  providedIn: 'root',
})
export class LuigiThemingService implements ThemingService {
  constructor(
    private luigiCoreService: LuigiCoreService,
    private authService: AuthService,
  ) {
    void this.checkAndUpdateStoredTheme();
  }

  getDefaultThemeId(): string {
    return DEFAULT_THEME_ID;
  }

  getAvailableThemes(): Theme[] {
    return staticThemes;
  }

  async checkAndUpdateStoredTheme(): Promise<void> {
    const user = this.authService.getUserInfo();
    const settings = (await userSettingsLocalStorage.read(user)) as any;
    const themeId = settings?.frame_appearance?.selectedTheme;
    // In case of any remains of old or wrong themes, clean up and set default theme
    if (themeId && !this.themeIsPartOfCoreThemes(themeId)) {
      await userSettingsLocalStorage.store({
        ...(settings as object),
        frame_appearance: { selectedTheme: DEFAULT_THEME_ID },
      });
    }
  }

  setThemeSheet(customAttr: string, url: string) {
    const oldSheet = document.head.querySelector(`link[${customAttr}]`);
    if (oldSheet) {
      oldSheet.parentNode?.removeChild(oldSheet);
    }
    const themeSheet = document.createElement('link');
    themeSheet.setAttribute('rel', 'stylesheet');
    themeSheet.setAttribute(customAttr, '');
    themeSheet.setAttribute('href', url);
    document.head.appendChild(themeSheet);
  }

  async applyTheme(id?: string, reset = true) {
    this.luigiCoreService.theming().setCurrentTheme(id);
    setTheme(id ?? '');
    this.setThemeSheet('sap-theme', `/themes/${id}/css_variables.css`);
    this.setThemeSheet('fd-theme', `/fundamental-styles-themes/${id}.css`);

    if (reset) {
      this.luigiCoreService.resetLuigi();
      await addNotificationsComponent(this.luigiCoreService, this.authService);
      // TODO add reset Joule
    }
  }

  async getStoredThemeOrDefault(): Promise<string> {
    const user = this.authService.getUserInfo();
    const settings = (await userSettingsLocalStorage.read(user)) as any;
    let themeId = settings?.frame_appearance?.selectedTheme || DEFAULT_THEME_ID;
    if (!this.themeIsPartOfCoreThemes(themeId)) {
      themeId = DEFAULT_THEME_ID;
    }

    return themeId;
  }

  themeIsPartOfCoreThemes(themeId: string): boolean {
    return !!this.getCoreThemes().find((t) => t.id === themeId);
  }

  getCoreThemes() {
    return staticThemes;
  }
}
